import {
  Description,
  TextWrapper,
  SectionContainer,
  TitleText,
  TitleWrapper,
  Underline,
  UnderlineContainer,
} from './styles';
import heroSectionDefaultBackgroundImage from '@assets/images/hero-section-default.png';
import Button from '@components/Button';
import { CenterItem } from '@components/LinearLayout';
import { getEventDetails } from '@store/slices/eventSlice';
import { useMobileWidth } from '@utils/hooks';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

interface HeroSectionProps {
  title: string;
  titleFontSize?: string;
  description: string;
  descriptionFontSize?: string;
  descriptionFontWeight?: string;
  backgroundImage?: string;
  backgroundSize?: string;
  backgroundPosition?: string;
  darkOverlay?: boolean;
  gradientOverlay?: boolean;
  lighterOverlay?: boolean;
  middleOverlay?: boolean;
  greyedOverlay?: boolean;
  button?: JSX.Element;
  buttonText?: string;
  buttonURL?: string;
  openInNewTab?: boolean;
  textColor?: string;
  height?: string;
}

const HeroSection = ({
  title,
  titleFontSize,
  description,
  descriptionFontSize,
  descriptionFontWeight,
  backgroundImage = heroSectionDefaultBackgroundImage,
  backgroundSize = '',
  backgroundPosition,
  darkOverlay = false,
  gradientOverlay = false,
  lighterOverlay = false,
  middleOverlay = false,
  greyedOverlay = false,
  button,
  buttonText,
  buttonURL,
  openInNewTab = true,
  textColor,
  height,
}: HeroSectionProps) => {
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const { design } = sluggedEvent || { design: {} };
  const theme = useTheme();
  const isMobile = useMobileWidth();

  return (
    <section>
      <SectionContainer
        backgroundImage={backgroundImage}
        backgroundSize={backgroundSize}
        backgroundPosition={backgroundPosition}
        darkOverlay={darkOverlay}
        gradientOverlay={gradientOverlay}
        lighterOverlay={lighterOverlay}
        middleOverlay={middleOverlay}
        greyedOverlay={greyedOverlay}
        height={height}
      >
        <TextWrapper>
          <TitleWrapper>
            <TitleText
              color={textColor || theme.color.white}
              fontSize={titleFontSize}
            >
              {title}
            </TitleText>
          </TitleWrapper>

          <UnderlineContainer>
            <Underline color={design.themeColor} />
          </UnderlineContainer>

          {description && (
            <Description
              color={textColor || theme.color.white}
              fontWeight={descriptionFontWeight}
              fontSize={descriptionFontSize}
            >
              {description.split('\n').map((line, index) => (
                <span key={index}>
                  {line}
                  <br />
                </span>
              ))}
            </Description>
          )}

          {button ||
            (buttonText && (
              <CenterItem $width="100%">
                <Button
                  title={buttonText}
                  backgroundColor={sluggedEvent?.design?.themeColor}
                  hoverBackgroundColor={theme.color.mobilityGrey}
                  textColor={theme.color.white}
                  borderRadius={theme.borderRadius.radius10}
                  margin="4rem 0 0 0"
                  padding="1.2rem 5.5rem"
                  width={isMobile ? '100%' : 'max-content'}
                  onClick={() => {
                    if (openInNewTab) {
                      window.open(
                        `${buttonURL ?? ''}`,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    } else {
                      if (buttonURL) {
                        window.location.href = buttonURL;
                      }
                    }
                  }}
                />
              </CenterItem>
            ))}
        </TextWrapper>
      </SectionContainer>
    </section>
  );
};

export default HeroSection;
